<template>
  <div :class="['ad-wrap', {'is-91xj': HNewGameUIConfig.newGameUIType === 3}]">
    <div v-if="!detail" class="ad-loading">加载中...</div>

    <template v-else>
      <!-- Banner：所有活动详情通用，seq 大的靠前；为空不显示。图片走 HCacheImage 解密 -->
      <div v-if="banners.length" class="ad-banner-wrap">
        <van-swipe v-if="banners.length > 1" :autoplay="3000" indicator-color="white" lazy-render>
          <van-swipe-item v-for="(b, i) in banners" :key="i">
            <HCacheImage :src="b.image_url" width="100%" height="100%" radius="10px" fit="cover"/>
          </van-swipe-item>
        </van-swipe>
        <HCacheImage v-else :src="banners[0].image_url" width="100%" height="100%" radius="10px" fit="cover"/>
      </div>

      <SignIn v-if="actType === 2" :activity-id="activityId" :detail="detail" @refresh="$emit('refresh')"/>

      <div v-else>
      <!-- 任务卡片列表 -->
      <div v-if="awards.length" class="ad-tasks">
        <div
            v-for="aw in awards"
            :key="aw.id"
            class="ad-task"
            @click="openPopup(aw)"
        >
          <div class="ad-task-info">
            <div class="ad-task-title-row">
              <span class="ad-task-title">{{ taskTitle(aw) }}</span>
              <img :src="helpIcon" class="ad-task-help" @click.stop="openPopup(aw)" alt="?"/>
            </div>
            <div class="ad-task-prog-row">
              <span class="ad-task-prog-label">当前进度:</span>
              <span class="ad-task-prog-current">{{ formatInt(aw.progress) }}/{{ formatInt(aw.tier_min) }}</span>
              <span class="ad-task-prog-sep"></span>
              <span class="ad-task-prog-label">奖励:</span>
              <span class="ad-task-prog-pct">{{ aw.award_text }}</span>
            </div>
            <div class="ad-task-bar">
              <div class="ad-task-bar-fill" :style="{width: percent(aw) + '%'}"></div>
            </div>
            <div class="ad-task-time">活动时间: {{ taskTimeText }}</div>
          </div>
          <div class="ad-task-btn-wrap">
            <button
                :class="['ad-task-btn', taskBtnClass(aw)]"
                :disabled="!aw.can_claim && !getClickItem(aw)"
                @click.stop="onTaskBtnClick(aw)"
            >{{ taskBtnText(aw) }}</button>
            <span v-if="aw.can_claim" class="ad-task-dot"></span>
          </div>
        </div>
      </div>

      <!-- 活动规则 -->
      <div v-if="descText" class="ad-rules">
        <div class="ad-rules-title">活动规则:</div>
        <div v-if="descIsHtml" v-html="descText" class="ad-rules-body"></div>
        <div v-else class="ad-rules-body ad-rules-body-text">{{ descText }}</div>
      </div>
      </div>
    </template>

    <!-- 任务详情底部弹窗 -->
    <van-popup v-model:show="popupVisible" position="bottom" round teleport="body">
      <div class="ad-popup" v-if="popupAward">
        <div class="ad-popup-title">{{ taskTitle(popupAward) }}</div>
        <div class="ad-popup-row">
          <span class="ad-popup-label">活动进度：</span>
          <span class="ad-popup-status">{{ popupAward.claimed ? '已完成' : '进行中' }}</span>
        </div>
        <div class="ad-popup-row">
          <span class="ad-popup-label">今日有效投注：</span>
          <span class="ad-popup-value">{{ formatInt(popupAward.progress) }}/{{ formatInt(popupAward.tier_min) }}</span>
        </div>
        <div class="ad-popup-bar-row">
          <div class="ad-popup-bar">
            <div class="ad-popup-bar-fill" :style="{width: percent(popupAward) + '%'}"></div>
          </div>
          <span class="ad-popup-bar-pct">已完成：{{ percent(popupAward) }}%</span>
        </div>
        <div class="ad-popup-divider"></div>
        <div v-if="popupRuleText" class="ad-popup-rules">
          <div class="ad-popup-rules-title">任务规则:</div>
          <div v-if="popupRuleIsHtml" v-html="popupRuleText" class="ad-popup-rules-body"></div>
          <div v-else class="ad-popup-rules-body ad-popup-rules-body-text">{{ popupRuleText }}</div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script setup>
import {ref, computed} from 'vue';
import SignIn from "@/components/pages/Activity/SignIn.vue";
import HCacheImage from "@/components/utils/HCacheImage.vue";
import helpIcon from "@/assets/icons/activity/activity_help.png";
import {useClickModal} from "@/hooks/useClickModal.js";
import HNewGameUIConfig from "@/components/utils/HNewGameUIConfig.js";

const clickModal = useClickModal();

const props = defineProps({
  activityId: [Number, String],
  detail: Object,
  onClaim: Function,
});
defineEmits(['refresh']);

const awards = computed(() => {
  const list = props.detail?.awards ?? [];
  return [...list].sort((a, b) => {
    const claimedDiff = (a.claimed ? 1 : 0) - (b.claimed ? 1 : 0);
    if (claimedDiff !== 0) return claimedDiff;
    return (b.seq ?? 0) - (a.seq ?? 0);
  });
});
const awardMode = computed(() => props.detail?.activity?.award_mode ?? 1);
const actType = computed(() => props.detail?.activity?.act_type ?? 0);
const descText = computed(() => props.detail?.activity?.desc_text ?? '');
const descIsHtml = computed(() => /<[a-z][\s\S]*>/i.test(descText.value));
const banners = computed(() => {
  const b = props.detail?.activity?.banner;
  if (!Array.isArray(b) || b.length === 0) return [];
  return [...b].sort((x, y) => (y.seq ?? 0) - (x.seq ?? 0));
});
// 任务规则：优先用每个任务自带的 help_text，空字符串时回退到活动大类的 desc_text
const popupRuleText = computed(() => popupAward.value?.help_text || descText.value);
const popupRuleIsHtml = computed(() => /<[a-z][\s\S]*>/i.test(popupRuleText.value));
function getClickItem(aw) {
  if (!aw) return null;
  if (aw.ClickItem?.ClickType) return aw.ClickItem;
  if (aw.click_type) return {ClickType: aw.click_type, ClickParam: aw.click_param, ClickLogin: aw.click_login};
  return null;
}

const popupVisible = ref(false);
const popupAward = ref(null);

function openPopup(aw) {
  popupAward.value = aw;
  popupVisible.value = true;
}

function onTaskBtnClick(aw) {
  if (aw.can_claim) {
    onClaimClick(aw);
    return;
  }
  const ci = getClickItem(aw);
  if (ci) {
    clickModal.onClick(ci);
  }
}

function formatInt(fen) {
  return Math.floor((fen ?? 0) / 100);
}

function percent(aw) {
  if (!aw?.tier_min) return 0;
  return Math.min(Math.round((aw.progress || 0) / aw.tier_min * 100), 100);
}

function taskTitle(aw) {
  return aw?.title ?? '';
}

function taskBtnText(aw) {
  if (aw.claimed) return '已领取';
  if (aw.can_claim) return '可领取';
  return aw.button_text || (getClickItem(aw) ? '去完成' : '未完成');
}

function taskBtnClass(aw) {
  if (aw.claimed) return 'ad-task-btn-done';
  if (aw.can_claim) return 'ad-task-btn-active';
  if (getClickItem(aw)) return 'ad-task-btn-todo';
  return 'ad-task-btn-done';
}

const taskTimeText = computed(() => {
  const t = props.detail?.activity?.end_tick;
  if (!t) return '永久';
  const d = new Date(t * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `截止到 ${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日 ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
});

function onClaimClick(aw) {
  props.onClaim?.(aw);
}
</script>

<style scoped>
.ad-wrap {
  padding: 0 0 20px;
  font-size: 13px;
  color: #333;
  background: #fff;
  min-height: 100%;
  border-radius: 5px;
  display: flow-root;
}

.ad-loading {
  text-align: center;
  color: #999;
  padding: 40px 0;
}

.ad-banner-wrap {
  margin: 10px;
  aspect-ratio: 750 / 240;
  border-radius: 10px;
  overflow: hidden;
}

.ad-banner-wrap :deep(.van-swipe),
.ad-banner-wrap :deep(.van-swipe__track),
.ad-banner-wrap :deep(.van-swipe-item) {
  height: 100%;
}

/* ===== 任务卡片 ===== */
.ad-tasks {
  background: #fafbfd;
  border-radius: 10px;
  padding: 4px 6px;
}

.ad-task {
  display: flex;
  align-items: center;
  background: rgb(239, 244, 252);
  border-radius: 10px;
  padding: 8px;
  margin-bottom: 8px;
  cursor: pointer;
}

.ad-task:last-child {
  margin-bottom: 0;
}

.ad-task-info {
  flex: 1;
  min-width: 0;
}

.ad-task-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.ad-task-title {
  font-size: 13px;
  font-weight: bold;
  color: #222;
}

.ad-task-help {
  width: 14px;
  height: 14px;
  display: inline-block;
  cursor: pointer;
}

.ad-task-prog-row {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  margin-bottom: 2px;
  white-space: nowrap;
}

.ad-task-prog-label {
  color: #999;
}

.ad-task-prog-current {
  color: #f5a623;
  font-weight: bold;
}

.ad-task-prog-sep {
  flex: 1;
}

.ad-task-prog-pct {
  color: #f5a623;
  font-weight: bold;
}

.ad-task-bar {
  height: 5px;
  background: #d8d8d8;
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 2px;
}

.ad-task-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #ffe6a8 0%, #f5a623 100%);
  border-radius: 5px;
  transition: width 0.3s;
}

.ad-task-time {
  font-size: 11px;
  color: #b0b0b0;
}

.ad-task-btn-wrap {
  position: relative;
  margin-left: 12px;
  flex-shrink: 0;
}

.ad-task-btn {
  border: none;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  outline: none;
  min-width: 70px;
}

.ad-task-btn-active {
  background: var(--ct-main-color);
  color: #fff;
}

.ad-task-btn-done {
  background: rgb(219, 219, 219);
  color: #fff;
}

.ad-task-btn-todo {
  background: var(--ct-main-color);
  color: #fff;
}

.ad-task-btn:disabled {
  cursor: not-allowed;
}

.ad-task-dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff4d4f;
}

/* ===== 活动规则 ===== */
.ad-rules {
  margin-top: 16px;
  padding: 4px 7px;
}

.ad-rules-title {
  color: var(--ct-main-color);
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 8px;
}

.ad-rules-body {
  font-size: 13px;
  color: #333;
  line-height: 1.7;
}

.ad-rules-body-text {
  white-space: pre-line;
}

.ad-rules-body :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
}

/* ===== 弹窗 ===== */
.ad-popup {
  padding: 24px 18px 28px;
  font-size: 13px;
}

.ad-popup-title {
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  color: #222;
  margin-bottom: 18px;
}

.ad-popup-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.ad-popup-label {
  font-size: 14px;
  color: #333;
  font-weight: bold;
}

.ad-popup-status {
  color: #2da566;
  font-weight: bold;
  font-size: 14px;
}

.ad-popup-value {
  color: #f5a623;
  font-weight: bold;
  font-size: 14px;
}

.ad-popup-bar-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 0 16px;
}

.ad-popup-bar {
  flex: 1;
  height: 8px;
  background: #d8d8d8;
  border-radius: 8px;
  overflow: hidden;
}

.ad-popup-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #ffe6a8 0%, #f5a623 100%);
  border-radius: 8px;
  transition: width 0.3s;
}

.ad-popup-bar-pct {
  font-size: 12px;
  color: #f5a623;
  font-weight: bold;
  white-space: nowrap;
}

.ad-popup-divider {
  height: 1px;
  background: #eee;
  margin: 6px 0 16px;
}

.ad-popup-rules-title {
  color: var(--ct-main-color);
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 8px;
}

.ad-popup-rules-body {
  font-size: 13px;
  color: #333;
  line-height: 1.7;
}

.ad-popup-rules-body-text {
  white-space: pre-line;
}

.ad-popup-rules-body :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
}

/* ===== 91xj 暗色覆盖（仅作用于详情页主体；签到 SignIn 由其自身处理；底部弹窗 teleport 到 body 外，本组件 scoped 不影响） ===== */
.ad-wrap.is-91xj {
  background: #1c1c1c;
  color: #ddd;
}

.is-91xj .ad-loading {
  color: #777;
}

.is-91xj .ad-tasks {
  background: #222;
}

.is-91xj .ad-task {
  background: #2a2a2a;
}

.is-91xj .ad-task-title {
  color: #fff;
}

.is-91xj .ad-task-prog-label {
  color: #888;
}

.is-91xj .ad-task-time {
  color: #777;
}

.is-91xj .ad-task-bar {
  background: #3a3a3a;
}

.is-91xj .ad-rules-body {
  color: #ccc;
}

.is-91xj .ad-task-btn-done {
  background: #3a3a3a;
  color: #888;
}
</style>
